import { HTMLAttributes, ReactNode } from 'react'

import LayoutWithNavigation from '@/components/layoutWithNavigation'
import STRINGS from '@/constants/responsibleDisclosure'

interface ParagraphProps {
  children: ReactNode
}

const Paragraph = ({ children }: ParagraphProps) => (
  <p className="text-base py-4">{children}</p>
)

interface AnchorProps extends HTMLAttributes<HTMLAnchorElement> {
  children: ReactNode
  href: string
  external?: boolean
}

const Anchor = ({ children, href, external }: AnchorProps) => {
  const attributes = external
    ? { target: '_blank', rel: 'noreferrer nofollow' }
    : {}
  return (
    <a
      href={href}
      className="hover:text-dehaat-green transition-all duration-300 font-natosans-medium underline"
      {...attributes}
    >
      {children}
    </a>
  )
}

const ResponsibleDisclosure = () => {
  return (
    <LayoutWithNavigation>
      <section className="px-6 my-4 max-w-7xl m-auto">
        <h1 className="leading-5 font-natosans-semibold text-[32px] py-4">
          Responsible Disclosure
        </h1>
        <Paragraph>
          Dehaat is committed to protecting its customers&apos; data and
          privacy. We also recognize the important role that security
          researchers play in helping us keep our systems secure. We therefore
          invite security researchers to responsibly disclose potential security
          vulnerabilities in our systems.
        </Paragraph>
        <Paragraph>
          If you believe you have found a security vulnerability in the
          Dehaat&apos;s systems, please contact us at{' '}
          <strong>
            <Anchor href="mailto:security@agrevolution.in">
              security@agrevolution.in
            </Anchor>
          </strong>
          . We will investigate any reported vulnerability and take appropriate
          steps to address the issue. We are committed to working with security
          researchers to ensure any vulnerability is properly resolved. Thank
          you in advance for your help in keeping our customers&apos; data and
          privacy secure.
        </Paragraph>
        <h2 className="leading-5 font-natosans-medium text-3xl py-4">Scope</h2>
        <ul className="list-disc list-inside text-base py-4">
          <li className="my-1">*.agrevolution.in</li>
          <li className="my-1">*.dehat.co</li>
          <li className="my-1">*.globalagricentral.com</li>
          <li className="my-1">
            Dehaat Partner Business App (
            <Anchor
              href="https://play.google.com/store/apps/details?id=com.intspvt.app.dehaat2&pli=1"
              external
            >
              DeHaat Partner Business App - Apps on Google Play
            </Anchor>
            )
          </li>
          <li className="my-1">
            AIMS App (
            <Anchor
              href="https://play.google.com/store/apps/details?id=com.dehaat.aims"
              external
            >
              AIMS - Apps on Google Play
            </Anchor>
            )
          </li>
          <li className="my-1">
            DeHaat Kisaan Farming Guide (
            <Anchor
              href="https://play.google.com/store/apps/details?id=app.intspvt.com.farmer"
              external
            >
              DeHaat Kisan: Farming Guide - Apps on Google Play
            </Anchor>
            )
          </li>
          <li className="my-1">
            Output Management System (
            <Anchor
              href="https://play.google.com/store/apps/details?id=com.dehaat.output"
              external
            >
              OMS - Output Management System - Apps on Google Play
            </Anchor>
            )
          </li>
          <li className="my-1">
            AgriCentral App (
            <Anchor
              href="https://play.google.com/store/apps/details?id=com.globalagricentral"
              external
            >
              AgriCentral - Apps on Google Play
            </Anchor>
            )
          </li>
        </ul>
        <h2 className="leading-5 font-natosans-medium text-3xl py-4">
          Reporting Guidelines
        </h2>
        <Paragraph>
          Please provide the following details on the report
        </Paragraph>
        <ul className="list-disc list-inside pb-4 text-base">
          <li className="my-1">
            Description and potential impact of the vulnerability;
          </li>
          <li className="my-1">
            A detailed description of the steps required to reproduce the
            vulnerability; and,
          </li>
          <li className="my-1">Where available, a video POC.</li>
          <li className="my-1">
            Email to{' '}
            <strong>
              <Anchor href="mailto:security@agrevolution.in">
                security@agrevolution.in
              </Anchor>
            </strong>
          </li>
          <p>
            <b className="font-natosans-bold">Note</b>: Only vulnerabilities
            deemed exploitable will be considered for a reward. The
            determination of exploitability and the acceptance of reported
            vulnerabilities lie solely at the discretion of the Dehaat Security
            Team.
          </p>
        </ul>
        <h2 className="leading-5 font-natosans-medium text-3xl py-4">Policy</h2>
        <Paragraph>We ask that:-</Paragraph>
        <ul className="pb-4 list-decimal list-inside text-base">
          <li className="my-1">
            Security researchers must not violate the privacy of our customers
            or disrupt the availability of our services.
          </li>
          <li className="my-1">
            Security researchers must conduct their activities in compliance
            with all applicable laws.
          </li>
          <li className="my-1">
            Security researchers are encouraged to disclose potential security
            vulnerabilities in a responsible manner and provide sufficient
            details to allow Dehaat to reproduce and resolve the issue.
          </li>
          <li className="my-1">
            Security researchers must not publicly disclose any potential
            security vulnerabilities until Dehaat has been given a reasonable
            amount of time to respond and remediate the issue.
          </li>
          <li className="my-1">
            Security researchers must not access or use any Dehaat customer data
            without permission.
          </li>
          <li className="my-1">
            Security researchers should not attempt to exploit a vulnerability
            or access any Dehaat systems without permission.
          </li>
          <li className="my-1">
            Security researchers should not attempt to reverse engineer any
            Dehaat code or systems without permission.
          </li>
          <li className="my-1">
            Security researchers must not modify any data.
          </li>
          <li className="my-1">
            <b className="font-natosans-bold">
              Issues from nuclei tool are known to us. Please do not report the
              issues from nuclei tools.
            </b>
          </li>
          <li className="my-1">
            <b className="font-natosans-bold">
              Only high severity exploitable issues are eligible for Hall of
              Fame.
            </b>
          </li>
        </ul>
        <h2 className="leading-5 font-natosans-medium text-3xl py-4">
          Hall of Fame
        </h2>
        <p>
          This section lists people who have had security reports accepted by
          the DeHaat security team.
        </p>
        <ul className="list-disc list-inside pb-4 text-base">
          <li className="my-1">
            <Anchor
              href="https://www.linkedin.com/in/nat%C3%A3-luiz-de-lima-9116bb2aa"
              external
            >
              Natã Luiz de Lima
            </Anchor>
          </li>
          <li className="my-1">
            <Anchor
              href="https://www.linkedin.com/in/yogesh-bhandage/"
              external
            >
              Yogesh Bhandage
            </Anchor>
          </li>
          <li className="my-1">
            <Anchor href="https://www.linkedin.com/in/eabubakr21/" external>
              Eslam Abu Bakr
            </Anchor>
          </li>
          <li className="my-1">
            <Anchor href="https://www.linkedin.com/in/shivangmauryaa/" external>
              Shivang Maurya
            </Anchor>
          </li>
          <li className="my-1">
            <Anchor href="https://www.linkedin.com/in/mnqazi/" external>
              Mo Nadeem
            </Anchor>
          </li>
          <li className="my-1">
            <Anchor
              href="https://www.linkedin.com/in/parth-narula-86283821a/"
              external
            >
              Parth Narula
            </Anchor>
          </li>
          <li className="my-1">
            <Anchor
              href="https://www.linkedin.com/in/gaurav-dalal-0434821b5/"
              external
            >
              Gaurav Dalal
            </Anchor>
          </li>
        </ul>
      </section>
    </LayoutWithNavigation>
  )
}

export async function getStaticProps() {
  return {
    props: {
      title: STRINGS.PAGE_TITLE,
      description: STRINGS.PAGE_DESCRIPTION,
    },
  }
}

export default ResponsibleDisclosure
